import Markets from '@/constants/Markets';
import {
  AppFilters,
  ComplianceDocument,
  MarketingAssetSchema,
} from '@/lib/types';
import { ReactNode } from 'react';

export type FileMarketItem = {
  key: number;
  icon?: ReactNode;
  label: ReactNode;
  text: string;
};

const getMarketDisplay = (marketName: string): FileMarketItem => ({
  key: 0,
  ...(Markets?.[marketName] || {
    label: marketName,
    text: marketName,
  }),
});

export type Mode = 'list' | 'grid';

export type FileGameRow = {
  id: number;
  name: string;
  group?: string | null;
  rtp?: number | null;
};

export type FileGameGroup = {
  key: string;
  name: string;
  rtps: number[];
};

type FilterGame = AppFilters['games'][number];

const EMPTY_GAME_INDEX = new Map<number, FilterGame>();

// One id -> game index per filters payload, shared by every card. The
// array identity is stable for as long as the ['filters'] query data is,
// and a card must not rebuild a map of every game on each render.
const gameIndexes = new WeakMap<object, Map<number, FilterGame>>();

const getGameIndex = (games: AppFilters['games'] | undefined) => {
  if (!games?.length) {
    return EMPTY_GAME_INDEX;
  }

  let index = gameIndexes.get(games);

  if (!index) {
    index = new Map(games.map((game) => [game.game_id, game]));
    gameIndexes.set(games, index);
  }

  return index;
};

export const getFileGames = (
  games: AppFilters['games'] | undefined,
  file: ComplianceDocument | MarketingAssetSchema,
): FileGameRow[] => {
  const byId = getGameIndex(games);

  // REST payloads carry group and RTP on the game itself; `/api/search` hits
  // carry neither, so those still resolve from the filters payload.
  if (file.games?.length) {
    return file.games
      .map((game) => {
        const match = byId.get(game.id);
        return {
          id: game.id,
          name: game.game_name || match?.game_name || '',
          group: game.game_group ?? match?.game_group,
          rtp: game.rtp ?? match?.rtp,
        };
      })
      .filter(({ name }) => name);
  }

  if ('game_ids' in file && file.game_ids?.length) {
    return file.game_ids
      .map((id): FileGameRow | null => {
        const match = byId.get(id);
        return match
          ? {
              id,
              name: match.game_name,
              group: match.game_group,
              rtp: match.rtp,
            }
          : null;
      })
      .filter((game): game is FileGameRow => game !== null);
  }

  return [];
};

// Each RTP version of a game is its own row, named after the version it
// carries ("3 Big Barrels Aztec 90"). `game_group` is the logical game
// behind them, but it isn't in every payload, so fall back to reading
// the version off the end of the name.
const GAME_RTP_SUFFIX = /^(.*\S)\s+(\d{2,3}(?:\.\d+)?)$/;

const splitGameRtpSuffix = (
  name: string,
): { base: string; rtp?: number } => {
  const match = GAME_RTP_SUFFIX.exec(name);

  if (!match) {
    return { base: name };
  }

  const value = Number(match[2]);

  return value >= 50 && value <= 100
    ? { base: match[1], rtp: value }
    : { base: name };
};

// A file linked to several RTP versions of one game repeats that game's
// name once per version. Collapse those into one entry per logical game,
// named the way game cards name it: game_group, falling back to the name
// without its version suffix.
export const groupFileGames = (
  rows: FileGameRow[],
): FileGameGroup[] => {
  const groups: FileGameGroup[] = [];
  const byKey = new Map<string, FileGameGroup>();

  rows.forEach((row) => {
    const { base, rtp: suffixRtp } = splitGameRtpSuffix(row.name);
    const key = row.group || base;
    let entry = byKey.get(key);

    if (!entry) {
      entry = { key, name: key, rtps: [] };
      byKey.set(key, entry);
      groups.push(entry);
    }

    // Show the version label, not the measured RTP: the "96" version of
    // a game can hold an RTP of 96.90, which rounds to a 97 that appears
    // nowhere else in the portal. The name carries the label, so prefer
    // it and truncate the measured value only when there is no suffix.
    const label =
      suffixRtp ?? (row.rtp != null ? Math.floor(row.rtp) : null);

    if (label != null && !entry.rtps.includes(label)) {
      entry.rtps.push(label);
    }
  });

  groups.forEach(({ rtps }) => rtps.sort((a, b) => a - b));

  return groups;
};

export const formatFileGameGroup = ({
  name,
  rtps,
}: FileGameGroup): string =>
  rtps.length ? `${name} — RTP ${rtps.join(', ')}` : name;

export const getFileMarkets = (
  markets: { market_id: number; market_name: string }[],
  file: ComplianceDocument | MarketingAssetSchema,
): FileMarketItem[] => {
  if (file.markets?.length) {
    return file.markets.map((market) => {
      const filterMarket = markets?.find(
        ({ market_id }) => market_id === market.id,
      );
      const marketName = filterMarket?.market_name || market.name;

      return {
        ...getMarketDisplay(marketName),
        key: market.id,
      };
    });
  }

  if ('market_ids' in file && file.market_ids?.length) {
    return file.market_ids
      .map((marketId) => {
        const market = markets?.find(
          ({ market_id }) => market_id === marketId,
        );
        if (!market) return null;

        return {
          ...getMarketDisplay(market.market_name),
          key: market.market_id,
        };
      })
      .filter((market): market is FileMarketItem => market !== null);
  }

  return [];
};
